/**
 * Salary Structure Simulator — pure compute helpers.
 *
 * All amounts are MONTHLY unless explicitly named ANNUAL_*.
 * CTC in formulas = monthly CTC for the simulation period.
 *
 * Confirmed rules:
 * 1. Basic is mandatory — max(structure amount, category MW) when MW applies.
 *    Basic is never a CTC balancing component.
 * 2. Absolute min CTC = Basic(MW) + PF/Gratuity/ESI employer on Basic only.
 * 3. Other CTC slots (earnings, reimbursements, benefits) fill by orderNo:
 *    Fixed slots pay full formula/manual. Balance CTC (autoAdjustCTC) slots use
 *    amount = min(formula/manual, remaining room). The last balancer absorbs leftover
 *    CTC (negative only if Profile CTC < absolute min). Reimbursements consume CTC
 *    before the final balancer so Special is reduced accordingly.
 */

import {
  isMonthlyVariableComponent,
  filterComponentsForPackage,
  resolveFixedPackageCtc,
  toMonthlyAmount,
} from "../../../../../utils/salaryStructureComponentRules";
import { computeStructurePfForPayMonth } from "./structurePfMonthCalc";

export function roundAmount(v) {
  return Math.round(Number(v) || 0);
}

export function parseMoneyInput(value) {
  if (value == null || value === "") return NaN;
  const cleaned = String(value).replace(/,/g, "").replace(/\s/g, "").trim();
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

export function toMonthlyCtc(rawCtc, ctcPeriod = "monthly") {
  const period = String(ctcPeriod || "monthly").toLowerCase();
  if (period === "annual" || period === "yearly") {
    return roundAmount(rawCtc / 12);
  }
  return roundAmount(rawCtc);
}

export function componentCodeKey(component) {
  return (component.code || component.name || "").toUpperCase().replace(/\s+/g, "_");
}

function hasCategory(component, key) {
  if (component?.categories && typeof component.categories === "object") {
    return !!component.categories[key];
  }
  const raw = String(component?.category || "").toUpperCase();
  if (key === "earning") return raw === "EARNING";
  if (key === "deduction") return raw === "DEDUCTION";
  if (key === "benefit") return raw === "BENEFIT" || raw === "EMPLOYER_BENEFIT";
  if (key === "reimbursement") return raw === "REIMBURSEMENT";
  return false;
}

export function isBasicLike(component) {
  const code = componentCodeKey(component);
  const name = String(component.name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return (
    code === "BASIC" ||
    code === "BASIC_SALARY" ||
    name === "basic" ||
    name === "basic salary" ||
    name === "basic pay"
  );
}

export function isHraLike(component) {
  const code = componentCodeKey(component);
  const name = String(component.name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return code === "HRA" || name === "hra" || name === "house rent allowance";
}

function isSpecialAllowanceLike(component) {
  const code = componentCodeKey(component);
  const name = String(component.name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  return (
    code === "SPECIAL_ALLOWANCE" ||
    code === "SPECIAL_ALLOWANCES" ||
    name === "special allowance" ||
    name === "special allowances"
  );
}

export function isEmployerShareComponent(component) {
  const n = String(component.name || "").toLowerCase();
  return n.includes("employer") || (n.includes("gratuity") && !n.includes("employee"));
}

/** Monthly Variable (legacy Basis=Recurrent) — not fixed structure slots. */
export function isRecurrentComponent(component) {
  return isMonthlyVariableComponent(component);
}

/** Payslip / waterfall order (ascending). Basic should be 1. */
export function getComponentOrderNo(component) {
  const n = Number(component?.orderNo ?? component?.sortOrder ?? component?.order);
  if (Number.isFinite(n)) return n;
  return 9999;
}

export function sortComponentsByOrder(components = []) {
  return [...components].sort((a, b) => {
    const aBasic = isBasicLike(a) ? 0 : 1;
    const bBasic = isBasicLike(b) ? 0 : 1;
    if (aBasic !== bBasic) return aBasic - bBasic;
    const oa = getComponentOrderNo(a);
    const ob = getComponentOrderNo(b);
    if (oa !== ob) return oa - ob;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
}

/** Earnings, reimbursements, and benefits all consume monthly CTC room (A in A+B). */
export function consumesCtcSlot(component) {
  return (
    hasCategory(component, "earning") ||
    hasCategory(component, "reimbursement") ||
    hasCategory(component, "benefit")
  );
}

export function sumCtcSlotAmount(components, amountsByComponentId, excludeId = null) {
  return components.reduce((sum, component) => {
    if (
      component.id === excludeId ||
      isRecurrentComponent(component) ||
      !consumesCtcSlot(component)
    ) {
      return sum;
    }
    return sum + (amountsByComponentId[component.id] || 0);
  }, 0);
}

/**
 * Excel-style: CTC*50, CTC*50%, 50*CTC → 50% of base (not ×50).
 */
export function normalizePercentStyleFormula(formula) {
  let text = String(formula || "");
  const bases = "CTC|MONTHLY_CTC|ANNUAL_CTC|BASIC|GROSS|HRA|Basic";

  // BASE * 50%  → (BASE*50/100)
  text = text.replace(
    new RegExp(`\\b(${bases})\\s*\\*\\s*(\\d{1,3})\\s*%`, "gi"),
    (_, base, num) => {
      const n = Number(num);
      if (n < 1 || n > 100) return `${base}*${num}%`;
      return `((${base})*${n}/100)`;
    }
  );

  // 50% * BASE → (BASE*50/100)
  text = text.replace(
    new RegExp(`\\b(\\d{1,3})\\s*%\\s*\\*\\s*(${bases})\\b`, "gi"),
    (_, num, base) => {
      const n = Number(num);
      if (n < 1 || n > 100) return `${num}%*${base}`;
      return `((${base})*${n}/100)`;
    }
  );

  // BASE * 50  → (BASE*50/100)   when not already /100
  text = text.replace(
    new RegExp(`\\b(${bases})\\s*\\*\\s*(\\d{1,3})(?!\\s*[/\\.])`, "gi"),
    (match, base, num, offset, full) => {
      const n = Number(num);
      if (n < 1 || n > 100) return match;
      const after = full.slice(offset + match.length);
      if (/^\s*\/\s*100\b/.test(after)) return match;
      if (/^\s*%/.test(after)) return match; // handled above
      return `((${base})*${n}/100)`;
    }
  );

  // 50 * BASE → (BASE*50/100)
  text = text.replace(
    new RegExp(`\\b(\\d{1,3})\\s*\\*\\s*(${bases})\\b`, "gi"),
    (match, num, base) => {
      const n = Number(num);
      if (n < 1 || n > 100) return match;
      return `((${base})*${n}/100)`;
    }
  );

  return text;
}

/**
 * Build formula/context variables from current amounts.
 * CTC / MONTHLY_CTC are always monthly; ANNUAL_CTC is monthly×12.
 */
export function syncContextFromAmounts(context, components, amountsByComponentId) {
  let gross = 0;
  components.forEach((component) => {
    const amount = amountsByComponentId[component.id] || 0;
    const code = componentCodeKey(component);
    context[component.name] = amount;
    if (code) context[code] = amount;
    if (isBasicLike(component)) {
      context.BASIC = amount;
      context.Basic = amount;
    }
    if (isHraLike(component)) {
      context.HRA = amount;
    }
    if (
      hasCategory(component, "earning") ||
      hasCategory(component, "benefit") ||
      hasCategory(component, "reimbursement")
    ) {
      gross += amount;
    }
  });
  context.GROSS = gross;
  return gross;
}

function evalFormula(formulaEngine, formula, context) {
  const normalized = normalizePercentStyleFormula(formula);
  if (!formulaEngine) return 0;
  const calculation = formulaEngine.calculateFormula(normalized, context);
  return roundAmount(calculation.success ? calculation.result || 0 : 0);
}

/**
 * Resolve manual / % / formula for one component (live context only).
 * Does NOT use stale Step-2 formulaResult (that was often annual/mock and broke monthly sim).
 */
export function resolveComponentAmount(component, context, monthlyCtc, formulaEngine) {
  if (component.calculationType === "formula" && component.selectedFormula?.formula) {
    return evalFormula(formulaEngine, component.selectedFormula.formula, context);
  }

  const hasPercent = component.manualPercent !== "" && component.manualPercent != null;
  const hasFixed = Number(component.manualAmount) > 0;
  const amountType = String(component.manualAmountType || "").toLowerCase();
  const usePercent =
    amountType === "percent" ||
    component.calculationMethod === "variable" ||
    (hasPercent && Number(component.manualPercent) > 0 && !hasFixed);

  if (usePercent) {
    const pct = Number(component.manualPercent) || 0;
    const basis = String(component.percentBasis || "CTC").toUpperCase();
    let base = monthlyCtc;
    if (basis === "BASIC") base = Number(context.BASIC ?? context.Basic ?? 0);
    else if (basis === "GROSS") base = Number(context.GROSS ?? 0);
    else if (basis === "ANNUAL_CTC" || basis === "ANNUAL") {
      base = Number(context.ANNUAL_CTC ?? monthlyCtc * 12);
    }
    return roundAmount((base * pct) / 100);
  }

  // Fixed / manual — amounts are always monthly
  return roundAmount(Number(component.manualAmount) || 0);
}

export function sumEarningAmount(components, amountsByComponentId, excludeId = null) {
  return components.reduce((sum, component) => {
    if (
      component.id === excludeId ||
      isRecurrentComponent(component) ||
      !hasCategory(component, "earning")
    ) {
      return sum;
    }
    return sum + (amountsByComponentId[component.id] || 0);
  }, 0);
}

export function sumPfBase(components, amountsByComponentId, excludeId = null) {
  return components.reduce((sum, component) => {
    if (
      isRecurrentComponent(component) ||
      !hasCategory(component, "earning") ||
      !component.pfApplicable ||
      component.id === excludeId
    ) {
      return sum;
    }
    return sum + (amountsByComponentId[component.id] || 0);
  }, 0);
}

export function sumGratuityBase(components, amountsByComponentId, excludeId = null) {
  return components.reduce((sum, component) => {
    if (
      isRecurrentComponent(component) ||
      !hasCategory(component, "earning") ||
      !component.gratuityApplicable ||
      component.id === excludeId
    ) {
      return sum;
    }
    return sum + (amountsByComponentId[component.id] || 0);
  }, 0);
}

export function resolvePfWageBase(components, amountsByComponentId, applicability, excludeId = null) {
  if (!applicability?.pfApplicable) return 0;
  const flagged = sumPfBase(components, amountsByComponentId, excludeId);
  if (flagged > 0) return flagged;
  const basic = components.find(
    (c) => c.id !== excludeId && hasCategory(c, "earning") && isBasicLike(c)
  );
  if (basic) return amountsByComponentId[basic.id] || 0;
  return sumEarningAmount(components, amountsByComponentId, excludeId);
}

export function resolveGratuityWageBase(
  components,
  amountsByComponentId,
  applicability,
  excludeId = null
) {
  if (!applicability?.gratuityApplicable) return 0;
  const flagged = sumGratuityBase(components, amountsByComponentId, excludeId);
  if (flagged > 0) return flagged;
  const basic = components.find(
    (c) => c.id !== excludeId && hasCategory(c, "earning") && isBasicLike(c)
  );
  if (basic) return amountsByComponentId[basic.id] || 0;
  return sumEarningAmount(components, amountsByComponentId, excludeId);
}

export function computeEmployerStatutory(pfBase, gratuityBase, gross, config, applicability) {
  const pfWage = Math.max(0, Number(pfBase) || 0);
  const pfWageCeiling =
    Number(config?.pf?.pfWageCeiling ?? config?.pf?.ceiling ?? 15000) || 15000;
  const epsWageCeiling = Number(config?.pf?.epsWageCeiling ?? 15000) || 15000;
  const edliWageCeiling = Number(config?.pf?.edliWageCeiling ?? 15000) || 15000;
  const applyPfCap = !!applicability?.ceilingPf;
  const epsApplicable = applicability?.epsApplicable === true;
  const epsAmountCap = 1250;

  // Ceiling PF On  → min(wage, PF Wage Ceiling) for employee + employer 12% + admin
  // Ceiling PF Off (POHW) → full PF wage for 12% / admin
  const wageForPfCap = applyPfCap ? Math.min(pfWage, pfWageCeiling) : pfWage;
  const wageForAdmin = wageForPfCap;
  // EPS uses its own statutory wage ceiling; EDLI for CTC package follows eligible PF wage
  // so full-month 25k → 12%+0.5%+0.5% = ₹3,250 (no legacy ₹75 hard-cap).
  const wageForEps = Math.min(pfWage, epsWageCeiling);
  void edliWageCeiling;

  const epsRate = Number(config?.pf?.eps ?? 8.33);
  const employerSharePct = Number(config?.pf?.employerShare ?? 12) / 100;
  const adminPct = Number(config?.pf?.adminCharges ?? 0.5);
  const edliPct = Number(config?.pf?.edliRate ?? 0.5);

  // Employer Total PF (12%) → EPS (capped) → EPF = Total − EPS (never × 3.67% directly)
  const employerShareAmount = applicability.pfApplicable
    ? roundAmount(wageForPfCap * employerSharePct)
    : 0;
  let epsEmployer = 0;
  if (applicability.pfApplicable && epsApplicable) {
    epsEmployer = roundAmount(wageForEps * (epsRate / 100));
    epsEmployer = Math.min(epsEmployer, epsAmountCap);
  }
  const epfEmployer = applicability.pfApplicable
    ? employerShareAmount - epsEmployer
    : 0;

  const adminCharges = roundAmount(wageForAdmin * (adminPct / 100));
  const edliCharges = roundAmount(wageForPfCap * (edliPct / 100));

  // CTC employer PF = Employer Share % + Admin + EDLI (rates from StatutoryRatesManager).
  // Employee deduction stays at employeeShare % only (no admin/EDLI).
  const pfEmployer = applicability.pfApplicable
    ? roundAmount(employerShareAmount + adminCharges + edliCharges)
    : 0;
  // Monthly gratuity provision on gratuity wage base (usually Basic only).
  // Statutory Rates "4.81%" already equals 15/26/12 — do NOT divide by 12 again.
  // Example: Basic 18,000 → 18000 × 4.81% = 865.8 → ₹866
  const gratuity = applicability.gratuityApplicable
    ? roundAmount((gratuityBase * (Number(config.gratuity.rate) || 4.81)) / 100)
    : 0;

  let esiEmployer = 0;
  if (applicability.esiApplicable) {
    const cutoff = Number(config.esi.cutoff ?? config.esi.ceiling) || 21000;
    const g = Math.max(0, Number(gross) || 0);
    const eligibility = Number(
      applicability.esiEligibilityGross != null
        ? applicability.esiEligibilityGross
        : g
    );
    // Covered when eligibility (fixed) ≤ cutoff; contribute on min(g, cutoff).
    if (eligibility > 0 && eligibility <= cutoff && g > 0) {
      const wage = Math.min(g, cutoff);
      const esiRate = (Number(config.esi.employerShare) || 3.25) / 100;
      esiEmployer = Math.ceil(wage * esiRate);
    }
  }

  return {
    pfEmployer,
    esiEmployer,
    gratuity,
    total: pfEmployer + esiEmployer + gratuity,
    pfBreakdown: {
      employerShare: applicability.pfApplicable ? employerShareAmount : 0,
      epfEmployer: applicability.pfApplicable ? epfEmployer : 0,
      epsEmployer: applicability.pfApplicable ? epsEmployer : 0,
      // Always equals Employer Total PF (12%), not independent 3.67%+8.33% sum
      epfEps: applicability.pfApplicable ? employerShareAmount : 0,
      adminCharges: applicability.pfApplicable ? adminCharges : 0,
      edliCharges: applicability.pfApplicable ? edliCharges : 0,
    },
  };
}

/** Single PF (Employer) CTC line = Employer Share % + Admin + EDLI. */
export function buildEmployerPfContributionRows(statutory, pfApplicable = true) {
  if (!pfApplicable) return [];
  const total = roundAmount(Number(statutory?.pfEmployer) || 0);
  return [{ name: "PF (Employer)", amount: total }];
}

/**
 * PF (Employee) deduction — PF wage base from PF-marked components.
 * Ceiling PF On  → min(PF wage, PF Wage Ceiling) × employeeShare%
 * Ceiling PF Off → full PF wage × employeeShare%
 * Same wage base as employer Share so Employee/Employer PF match when rates are equal.
 */
export function computePfEmployeeDeduction(pfBase, config, applicability = {}) {
  if (!applicability?.pfApplicable) return 0;
  const wage = Math.max(0, Number(pfBase) || 0);
  console.log("wage", wage);
  const employeeSharePct = Number(config?.pf?.employeeShare ?? 12) / 100;
  const pfWageCeiling =
    Number(config?.pf?.pfWageCeiling ?? config?.pf?.ceiling ?? 15000) || 15000;
  console.log("pfWageCeiling", pfWageCeiling);
  const wageForEmployee = applicability?.ceilingPf
    ? Math.min(wage, pfWageCeiling)
    : wage;
  return roundAmount(wageForEmployee * employeeSharePct);
}

/** Employee + employer PF on the same wage base (for display / breakdown). */
export function computePfAmounts(pfBase, config, applicability = {}) {
  const wage = Math.max(0, Number(pfBase) || 0);
  const employee = computePfEmployeeDeduction(wage, config, applicability);
  const employerSide = computeEmployerStatutory(wage, 0, 0, config, {
    ...applicability,
    esiApplicable: false,
    gratuityApplicable: false,
  });
  return {
    pfWageBase: wage,
    employee,
    employer: employerSide.pfEmployer,
    breakdown: employerSide.pfBreakdown,
  };
}

/**
 * Minimum monthly/annual CTC when category minimum wages apply.
 * Floor = MW (as Basic) + PF employer + Gratuity + ESI employer (when MW gross ≤ ESI cutoff).
 * Structure formula earnings (HRA etc.) are NOT part of this legal floor — those may
 * still force a higher practical CTC and show as a negative balance component.
 */
export function computeMinimumCtcFromMw(mwAmount, config, applicability = {}) {
  const mw = roundAmount(Number(mwAmount) || 0);
  if (mw <= 0 || !config) return null;

  const esiCutoff = Number(config.esi?.cutoff ?? config.esi?.ceiling) || 21000;
  const pfOn = applicability.pfApplicable !== false;
  const gratuityOn = applicability.gratuityApplicable !== false;
  // Include ESI in the floor when flagged on, or when MW alone is ESI-eligible (auto).
  const esiOn =
    applicability.esiApplicable === true ||
    (applicability.esiApplicable !== false && mw > 0 && mw <= esiCutoff);

  const statutory = computeEmployerStatutory(mw, mw, mw, config, {
    pfApplicable: pfOn,
    esiApplicable: esiOn,
    gratuityApplicable: gratuityOn,
    ceilingPf: applicability.ceilingPf === true,
  });

  const monthly = roundAmount(mw + statutory.total);
  return {
    monthly,
    annual: roundAmount(monthly * 12),
    minimumWage: mw,
    pfEmployer: statutory.pfEmployer,
    gratuity: statutory.gratuity,
    esiEmployer: statutory.esiEmployer,
    esiIncluded: esiOn && statutory.esiEmployer > 0,
  };
}

function estimateEmployerCost(
  components,
  amountsByComponentId,
  config,
  applicability,
  excludeId = null,
  { statutoryConfigJson = null, payMonth = null } = {}
) {
  // Balance/Special: include in PF/ESI gross only when ≥ 0. Negative residual must not
  // shrink statutory bases (that caused Profile CTC ≠ A+B).
  const amounts = { ...amountsByComponentId };
  if (excludeId != null) {
    amounts[excludeId] = Math.max(0, Number(amountsByComponentId[excludeId]) || 0);
  }
  const gross = sumCtcSlotAmount(components, amounts);
  const pfBase = resolvePfWageBase(components, amounts, applicability);
  const gratuityBase = resolveGratuityWageBase(components, amounts, applicability);
  let statutory = computeEmployerStatutory(pfBase, gratuityBase, gross, config, applicability);
  // Mid-month PF ceiling (e.g. 17-Sep) — same segment package as Process for full month.
  const monthPf = computeStructurePfForPayMonth({
    pfWageMonthly: pfBase,
    statutoryConfigJson,
    payMonth,
    applicability,
  });
  if (monthPf && (monthPf.employer > 0 || monthPf.employee > 0)) {
    statutory = {
      ...statutory,
      pfEmployer: monthPf.employer,
      total: roundAmount(monthPf.employer + statutory.esiEmployer + statutory.gratuity),
      pfBreakdown: {
        ...(statutory.pfBreakdown || {}),
        employerShare: monthPf.employer,
      },
      _pfEmployeeOverride: monthPf.employee,
      _pfSegmentCount: monthPf.segmentCount,
    };
  }

  let employerFromComponents = 0;
  components.forEach((c) => {
    // CTC-slot components are already in the slot sum — counting them here too double-reduces the balancer.
    if (isEmployerShareComponent(c) && !consumesCtcSlot(c)) {
      employerFromComponents += amounts[c.id] || 0;
    }
  });

  return {
    statutory,
    employerFromComponents,
    total: statutory.total + employerFromComponents,
  };
}

/**
 * Absolute minimum CTC when MW applies = MW Basic + employer on Basic only
 * (HRA/Special are not required at the floor — they fill by order when CTC rises).
 */
export function computeStructureMinimumCtc({
  statutoryConfig,
  applicability,
  minimumWageAmount = null,
}) {
  return computeMinimumCtcFromMw(minimumWageAmount, statutoryConfig, applicability || {});
}

/**
 * Full simulator compute — always monthly amounts.
 * Waterfall: Basic → other CTC slots (earnings/reimbursements/benefits) by orderNo;
 * fixed slots consume room first; last autoAdjustCTC balancer absorbs leftover.
 */
export function computeSalaryStructureSimulation({
  components = [],
  salaryType = "ctc",
  ctcValue,
  ctcPeriod = "monthly",
  statutoryConfig,
  applicability,
  formulaEngine,
  minimumWageAmount = null,
  asOfDate = null,
  performanceBonusAmount = 0,
  performanceBonusPeriod = "annual",
  /** Monthly Fixed-CTC reserve for Employer NPS + Meal voucher (structure options). */
  fixedCtcReserveMonthly = 0,
  /** (basicMonthly) => monthly Fixed-CTC add-ons (Employer NPS / Meal) taken from Balance CTC only. */
  addonReserveFromBasic = null,
  /** Full statutory config (with history) — enables mid-month PF segments for pay month. */
  statutoryConfigJson = null,
}) {
  const payMonthYm = asOfDate ? String(asOfDate).slice(0, 7) : null;
  const empCostOpts = { statutoryConfigJson, payMonth: payMonthYm };
  const rawParsed = parseMoneyInput(ctcValue);
  const rawCtc = Number.isFinite(rawParsed) && rawParsed > 0 ? rawParsed : 50000;

  // Total CTC/Gross → Fixed package after Performance Bonus carve-out
  const packageSplit = resolveFixedPackageCtc({
    totalAmount: rawCtc,
    totalPeriod: ctcPeriod === "monthly" ? "monthly" : "annual",
    performanceBonusAmount,
    performanceBonusPeriod:
      performanceBonusPeriod === "monthly" ? "monthly" : "annual",
  });
  const reserve = Math.max(0, Number(fixedCtcReserveMonthly) || 0);
  const monthlyCtc = roundAmount(Math.max(0, packageSplit.fixedAnnual / 12 - reserve));
  const annualCtc = roundAmount(monthlyCtc * 12);
  const totalMonthlyCtc = toMonthlyAmount(rawCtc, ctcPeriod === "monthly" ? "monthly" : "annual");
  const config = statutoryConfig;
  const context = {
    CTC: monthlyCtc,
    MONTHLY_CTC: monthlyCtc,
    ANNUAL_CTC: annualCtc,
    TOTAL_CTC: totalMonthlyCtc,
    TOTAL_ANNUAL_CTC: packageSplit.totalAnnual,
    PERFORMANCE_BONUS: toMonthlyAmount(
      performanceBonusAmount,
      performanceBonusPeriod === "monthly" ? "monthly" : "annual"
    ),
    PERFORMANCE_BONUS_ANNUAL: packageSplit.pbAnnual,
    FIXED_CTC: monthlyCtc,
    FIXED_ANNUAL_CTC: annualCtc,
    BASIC: 0,
    HRA: 0,
    GROSS: 0,
  };
  const amountsByComponentId = {};
  const isCtcMode = String(salaryType || "ctc").toLowerCase() !== "gross";
  const mwFloor =
    minimumWageAmount != null && Number.isFinite(Number(minimumWageAmount))
      ? roundAmount(Number(minimumWageAmount))
      : null;

  // Fixed + active-as-of only; Monthly Variable stays on template for payroll inputs
  const structureComponents = filterComponentsForPackage(
    components || [],
    asOfDate || new Date().toISOString().slice(0, 10)
  );
  const orderedAll = sortComponentsByOrder(structureComponents);
  const ctcSlotComponents = orderedAll.filter((c) => consumesCtcSlot(c));
  const basicComponent =
    ctcSlotComponents.find((c) => hasCategory(c, "earning") && isBasicLike(c)) || null;
  const otherCtcSlots = ctcSlotComponents.filter(
    (c) => !basicComponent || c.id !== basicComponent.id
  );
  const balancers = otherCtcSlots.filter((c) => !!c.autoAdjustCTC);
  // Only components explicitly marked "Balance CTC" absorb leftover (or deficit).
  const lastBalancerId =
    balancers.length > 0 ? balancers[balancers.length - 1].id : null;
  const nonBalancerSlots = otherCtcSlots.filter((c) => c.id !== lastBalancerId);
  const balancerSlots = lastBalancerId
    ? otherCtcSlots.filter((c) => c.id === lastBalancerId)
    : [];
  const waterfallSlots = [...nonBalancerSlots, ...balancerSlots];
  const postWaterfall = orderedAll.filter(
    (c) => !consumesCtcSlot(c) && !c.autoAdjustCTC
  );

  // --- Pass A: Basic (never a balancer) ---
  if (basicComponent) {
    let basicAmt = resolveComponentAmount(basicComponent, context, monthlyCtc, formulaEngine);
    if (isCtcMode && basicAmt > monthlyCtc * 1.01) {
      const fixed = Number(basicComponent.manualAmount) || 0;
      if (
        basicComponent.calculationType !== "formula" &&
        fixed > monthlyCtc * 2 &&
        Math.abs(fixed / 12 - monthlyCtc * 0.5) < monthlyCtc * 0.6
      ) {
        basicAmt = roundAmount(fixed / 12);
      } else if (basicAmt > monthlyCtc) {
        basicAmt = roundAmount(monthlyCtc);
      }
    }
    if (mwFloor != null) {
      basicAmt = Math.max(basicAmt, mwFloor);
    }
    amountsByComponentId[basicComponent.id] = basicAmt;
    syncContextFromAmounts(context, [basicComponent], amountsByComponentId);
  }

  const addonReserve =
    isCtcMode && typeof addonReserveFromBasic === "function"
      ? Math.max(
          0,
          roundAmount(
            addonReserveFromBasic(basicComponent ? amountsByComponentId[basicComponent.id] || 0 : 0)
          )
        )
      : 0;

  let statutoryEmployerUsedForBalance = { pfEmployer: 0, esiEmployer: 0, gratuity: 0, total: 0 };

  // --- Pass B: waterfall — non-balancers (incl. reimbursements) then balancers ---
  for (let outer = 0; outer < 12; outer += 1) {
    let changed = false;

    waterfallSlots.forEach((c) => {
      amountsByComponentId[c.id] = 0;
    });

    for (let idx = 0; idx < waterfallSlots.length; idx += 1) {
      const component = waterfallSlots[idx];
      const isLastBalancer = lastBalancerId != null && component.id === lastBalancerId;

      for (let j = idx + 1; j < waterfallSlots.length; j += 1) {
        amountsByComponentId[waterfallSlots[j].id] = 0;
      }

      const otherSum = sumCtcSlotAmount(structureComponents, amountsByComponentId, component.id);
      let employer = estimateEmployerCost(
        structureComponents,
        amountsByComponentId,
        config,
        applicability,
        lastBalancerId,
        empCostOpts
      );
      statutoryEmployerUsedForBalance = employer.statutory;

      let remaining = isCtcMode
        ? roundAmount(monthlyCtc - otherSum - employer.total - addonReserve)
        : roundAmount(monthlyCtc - otherSum);

      let wanted = resolveComponentAmount(component, context, monthlyCtc, formulaEngine);
      if (isCtcMode && wanted > monthlyCtc && wanted > 0) {
        wanted = roundAmount(monthlyCtc);
      }

      let next;
      if (!isCtcMode) {
        next = Math.max(0, wanted);
      } else if (isLastBalancer) {
        // Final CTC balancer — absorbs leftover (may go negative when fixed slots + employer exceed CTC).
        next = remaining;
      } else if (component.autoAdjustCTC) {
        // Earlier Balance CTC slots: formula/manual is a CAP only — never exceed remaining room.
        // Room is net of fixed slots placed later (e.g. Statutory Bonus) so they are paid in full
        // and this slot takes the leftover instead of pushing the final balancer negative.
        const laterFixed = waterfallSlots
          .slice(idx + 1)
          .filter((c) => !c.autoAdjustCTC && c.id !== lastBalancerId);
        laterFixed.forEach((c) => {
          const w = resolveComponentAmount(c, context, monthlyCtc, formulaEngine);
          amountsByComponentId[c.id] = Math.max(0, w > monthlyCtc ? roundAmount(monthlyCtc) : w);
        });
        if (laterFixed.length) {
          const oSum0 = sumCtcSlotAmount(structureComponents, amountsByComponentId, component.id);
          const emp0 = estimateEmployerCost(
            structureComponents,
            amountsByComponentId,
            config,
            applicability,
            lastBalancerId,
            empCostOpts
          );
          remaining = roundAmount(monthlyCtc - oSum0 - emp0.total - addonReserve);
        }
        let candidate = Math.min(Math.max(0, wanted), Math.max(0, remaining));
        for (let k = 0; k < 6; k += 1) {
          amountsByComponentId[component.id] = candidate;
          const oSum = sumCtcSlotAmount(structureComponents, amountsByComponentId, component.id);
          const empK = estimateEmployerCost(
            structureComponents,
            amountsByComponentId,
            config,
            applicability,
            lastBalancerId,
            empCostOpts
          );
          const remK = roundAmount(monthlyCtc - oSum - empK.total - addonReserve);
          const refined = Math.min(Math.max(0, wanted), Math.max(0, remK));
          if (Math.abs(refined - candidate) < 1) {
            candidate = refined;
            break;
          }
          candidate = refined;
        }
        laterFixed.forEach((c) => {
          amountsByComponentId[c.id] = 0;
        });
        next = candidate;
      } else {
        // Fixed / manual / formula — always pay full configured amount (never reduce Fuel etc.).
        next = Math.max(0, wanted);
      }

      const prev = amountsByComponentId[component.id] || 0;
      amountsByComponentId[component.id] = next;
      if (Math.abs(prev - next) >= 0.5) changed = true;
      syncContextFromAmounts(context, ctcSlotComponents, amountsByComponentId);

      if (isLastBalancer && isCtcMode) {
        for (let k = 0; k < 6; k += 1) {
          amountsByComponentId[component.id] = 0;
          const oSum = sumCtcSlotAmount(structureComponents, amountsByComponentId, component.id);
          employer = estimateEmployerCost(
            structureComponents,
            amountsByComponentId,
            config,
            applicability,
            component.id,
            empCostOpts
          );
          statutoryEmployerUsedForBalance = employer.statutory;
          const rem = roundAmount(monthlyCtc - oSum - employer.total - addonReserve);
          amountsByComponentId[component.id] = rem;
          employer = estimateEmployerCost(
            structureComponents,
            amountsByComponentId,
            config,
            applicability,
            component.id,
            empCostOpts
          );
          statutoryEmployerUsedForBalance = employer.statutory;
          const rem2 = roundAmount(monthlyCtc - oSum - employer.total - addonReserve);
          if (Math.abs(rem2 - rem) < 1) {
            amountsByComponentId[component.id] = rem2;
            break;
          }
          amountsByComponentId[component.id] = rem2;
        }
      }
    }

    syncContextFromAmounts(context, structureComponents, amountsByComponentId);

    // Do not reduce fixed/non-balancer components when CTC is short — leave deficit on balancer.
    if (!changed && outer > 0) break;
  }

  postWaterfall.forEach((component) => {
    amountsByComponentId[component.id] = resolveComponentAmount(
      component,
      context,
      monthlyCtc,
      formulaEngine
    );
  });

  {
    const employer = estimateEmployerCost(
      structureComponents,
      amountsByComponentId,
      config,
      applicability,
      lastBalancerId,
      empCostOpts
    );
    statutoryEmployerUsedForBalance = employer.statutory;
  }

  syncContextFromAmounts(context, structureComponents, amountsByComponentId);

  return {
    context,
    amountsByComponentId,
    monthlyCtc,
    annualCtc,
    totalAnnualCtc: packageSplit.totalAnnual,
    performanceBonusAnnual: packageSplit.pbAnnual,
    fixedAnnualCtc: packageSplit.fixedAnnual,
    packageSplitError: packageSplit.error || null,
    config,
    statutoryEmployerUsedForBalance,
    balanceComponentId: lastBalancerId,
    minimumWageApplied: mwFloor,
    basicAmount: basicComponent ? amountsByComponentId[basicComponent.id] || 0 : null,
  };
}
